import { useDisableTeam } from "@/components/page/disableTeam";
import { useOutletContext } from "react-router-dom";
import type { MeetingRunContext } from "./MeetingRunContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { useRef } from "react";
import { TextAreaAuto } from "@repo/ui/components/Base/TextAreaAuto";
import { Button } from "@repo/ui/components/Base/Button";
import { cn } from "@repo/shared/utils";
import { SelectPresenter } from "@/components/common/SelectPresenter";
import { CheckIcon } from "lucide-react";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { CollapsiblePanel } from "./CollapsiblePanel";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";

export const MeetingRunNoApprovalItemSection = () => {
  useDisableTeam();
  const {
    tenantId,
    meeting,
    agendaItem: _item,
    mutationSystem,
    noTimestamps,
  } = useOutletContext() as MeetingRunContext;

  const mutationKey = "agendaItem:" + _item!.id;

  const agendaItemMutation: Mutator<AgendaItemDto> = useFutureCallback(
    async (id, data) => {
      await agendaItemRepo.update(tenantId, id, data);
    }
  );
  const agendaItem = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _item!
  );

  const startTimestamp = useRef(Date.now());

  const isComplete = !!agendaItem.review?.timestamp;

  return (
    <div className="w-full flex flex-col gap-y-2 flex-grow">
      {/* fixed header */}
      <div className="flex-shrink-0">
        {/* <h1 className="text-3xl mb-2">{agendaItem.name}</h1> */}
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-end w-full px-1 pb-2">
          <div className="justify-self-end whitespace-nowrap">Description</div>

          <div className="justify-self-end text-center text-base font-medium max-w-[80%] mx-auto break-words leading-snug">
            {agendaItem.name}
          </div>

          <div className="opacity-0 select-none">Description</div>
        </div>
        <TextAreaAuto
          id="description"
          className="mb-2"
          value={agendaItem.description ?? ""}
          onChange={(evt) => {
            mutationSystem.debounce(
              mutationKey,
              agendaItemMutation,
              { description: evt.target.value },
              agendaItem.id
            );
          }}
        />
      </div>

      {/* scrolled content */}
      <div className="flex flex-col h-0 flex-grow overflow-auto px-2">
        <div>
          <CollapsiblePanel
            defaultOpen={false}
            className="mb-4"
            title="Details"
          >
            <h1 className="mt-2 text-2xl font-bold">Script</h1>
            <TextAreaAuto
              id="script"
              className="mb-2"
              value={agendaItem.script ?? ""}
              onChange={(evt) => {
                mutationSystem.debounce(
                  mutationKey,
                  agendaItemMutation,
                  { script: evt.target.value },
                  agendaItem.id
                );
              }}
            />

            <SelectPresenter
              label="Presenter"
              className="mb-2"
              value={agendaItem.presenter}
              onChange={(presenter) => {
                mutationSystem.debounce(
                  mutationKey,
                  agendaItemMutation,
                  { presenter },
                  agendaItem.id
                );
              }}
              team={agendaItem.teamId}
            />

            <TextAreaAuto
              id="comment"
              label="Staff Comment"
              className="mb-2"
              value={agendaItem.comment ?? ""}
              onChange={(evt) => {
                mutationSystem.debounce(
                  mutationKey,
                  agendaItemMutation,
                  { comment: evt.target.value },
                  agendaItem.id
                );
              }}
            />
          </CollapsiblePanel>
        </div>

        <TextArea
          id="comments"
          label="Discussion Summary"
          autoFocus
          className="h-40 mb-2"
          value={agendaItem.review?.comments ?? ""}
          onChange={(evt) => {
            mutationSystem.debounce(
              mutationKey,
              agendaItemMutation,
              {
                review: {
                  ...agendaItem.review,
                  meetingId: meeting.id,
                  comments: evt.target.value,
                },
              },
              agendaItem.id
            );
          }}
        />
        <Button
          type="button"
          className={cn(
            "border-[2px] border-tertiary w-60",
            isComplete
              ? "bg-tertiary hover:bg-tertiary/90"
              : "bg-transparent hover:bg-transparent text-tertiary"
          )}
          onClick={() => {
            const add = !agendaItem.review?.timestamp;
            const timestamps = noTimestamps
              ? {}
              : {
                  timestamp: add ? startTimestamp.current : undefined,
                  timestampEnd: add ? Date.now() : undefined,
                };

            mutationSystem.debounce(
              mutationKey,
              agendaItemMutation,
              {
                review: {
                  ...agendaItem.review,
                  meetingId: meeting.id,
                  ...timestamps,
                },
              },
              agendaItem.id
            );
          }}
        >
          {isComplete ? (
            <>
              <CheckIcon size={20} className="mr-2" /> Completed
            </>
          ) : (
            <>Click to Mark Complete</>
          )}
        </Button>
      </div>
    </div>
  );
};
